import type {
  TcTechnicalSummaryBlockGroup,
  TcTechnicalSummaryProjection,
} from "../types/tcTechnicalSummary";

export interface TcTechnicalSummaryPage {
  groups: Array<TcTechnicalSummaryBlockGroup & { continued?: boolean }>;
  first: boolean;
  final: boolean;
}

const FIRST_PAGE_UNITS = 43;
const FOLLOWING_PAGE_UNITS = 30;
const FINAL_SECTION_UNITS = 11;
const MINIMUM_PAGE_UNITS = 8;
const OVERSIZED_GROUP_HEADER_UNITS = 15;

export function paginateTcTechnicalSummary(
  projection: TcTechnicalSummaryProjection
): TcTechnicalSummaryPage[] {
  const pages: TcTechnicalSummaryPage[] = [
    { groups: [], first: true, final: false },
  ];
  let remaining = firstPageUnits(projection);

  projection.groups.forEach((group) => {
    const cost = groupUnits(group);
    if (cost <= remaining) {
      pages[pages.length - 1].groups.push(group);
      remaining -= cost;
      return;
    }
    if (cost <= FOLLOWING_PAGE_UNITS) {
      pages.push({ groups: [group], first: false, final: false });
      remaining = FOLLOWING_PAGE_UNITS - cost;
      return;
    }
    if (pages[pages.length - 1].groups.length > 0) {
      pages.push({ groups: [], first: false, final: false });
      remaining = FOLLOWING_PAGE_UNITS;
    }
    const chunks = splitLargeGroup(group, remaining);
    chunks.forEach((chunk, index) => {
      if (index > 0) {
        pages.push({ groups: [], first: false, final: false });
      }
      pages[pages.length - 1].groups.push(chunk);
      remaining = FOLLOWING_PAGE_UNITS - groupUnits(chunk);
    });
  });

  if (remaining < FINAL_SECTION_UNITS) {
    pages.push({ groups: [], first: false, final: true });
  } else {
    pages[pages.length - 1].final = true;
  }
  return pages;
}

function groupUnits(group: TcTechnicalSummaryBlockGroup): number {
  const headingWrap = Math.max(
    wrappedLineCount(group.materialDescription, 42),
    wrappedLineCount(group.sourceDescription, 55)
  );
  const rowUnits = group.stains.reduce(
    (sum, row) => sum + Math.max(1, Math.ceil(row.stainName.length / 55)),
    0
  );
  return 4 + headingWrap + rowUnits;
}

function firstPageUnits(projection: TcTechnicalSummaryProjection): number {
  const wrappedValues: Array<[string | null, number]> = [
    [projection.patient.name, 28],
    [projection.patient.dateOfBirth, 18],
    [projection.externalCaseNumber, 24],
    [projection.caseNumber, 24],
    [projection.patient.medicalRecordNumber, 45],
    [projection.physician.name, 60],
    [projection.physician.name, 60],
    [projection.physician.phone, 18],
    [projection.physician.fax, 18],
    [projection.clinicalInformation, 100],
  ];
  const extraLines = wrappedValues.reduce(
    (sum, [value, charactersPerLine]) =>
      sum + Math.max(0, wrappedLineCount(value, charactersPerLine) - 1),
    0
  );

  return Math.max(MINIMUM_PAGE_UNITS, FIRST_PAGE_UNITS - extraLines);
}

function wrappedLineCount(
  value: string | null,
  charactersPerLine: number
): number {
  return Math.max(1, Math.ceil((value?.length ?? 0) / charactersPerLine));
}

function splitLargeGroup(
  group: TcTechnicalSummaryBlockGroup,
  firstCapacity: number
): Array<TcTechnicalSummaryBlockGroup & { continued?: boolean }> {
  const chunks: Array<TcTechnicalSummaryBlockGroup & { continued?: boolean }> =
    [];
  let rows = [...group.stains];
  let capacity = Math.max(7, firstCapacity);
  while (rows.length > 0) {
    const headerUnits = OVERSIZED_GROUP_HEADER_UNITS;
    let used = headerUnits;
    let take = 0;
    while (take < rows.length) {
      const rowUnits = Math.max(1, Math.ceil(rows[take].stainName.length / 55));
      if (take > 0 && used + rowUnits > capacity) break;
      used += rowUnits;
      take += 1;
    }
    chunks.push({
      ...group,
      id: `${group.id}-${chunks.length + 1}`,
      stains: rows.slice(0, take),
      continued: chunks.length > 0,
    });
    rows = rows.slice(take);
    capacity = FOLLOWING_PAGE_UNITS;
  }
  return chunks;
}
